import { useEffect, useMemo, useRef, useState } from 'react';
import { computePhase, toTs } from '@jobfair/shared';
import { Site } from './components/Site';
import { Toasts } from './components/common';
import { env } from './lib/env';
import type { FeedClient } from './lib/feed';
import { useFeed, useNow } from './lib/hooks';
import { createTracker, type Tracker } from './lib/tracker';
import { TrackerProvider } from './lib/trackerContext';
import type { LiveState } from './types';
import { DemoBar } from './components/DemoBar';

const STALE_AFTER_MS = 90_000;

/** `?t=2026-11-30T17:30` (Israel time) freezes the page to a moment of the fair. Demo builds only. */
function demoOffsetFromUrl(): number | null {
  if (!env.demo) return null;
  const t = new URLSearchParams(location.search).get('t');
  const m = t && /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})$/.exec(t);
  if (!m) return null;
  const target = toTs(m[1], m[2]);
  return Number.isFinite(target) ? target - Date.now() : null;
}

export function App({ feed, tracker: injected }: { feed: FeedClient; tracker?: Tracker }) {
  const f = useFeed(feed);
  const [demoOffset, setDemoOffset] = useState<number | null>(demoOffsetFromUrl);
  const now = useNow(f.offset, demoOffset);

  const st: LiveState | null = f.state;
  const ph = useMemo(() => (st ? computePhase(st, now) : null), [st, now]);

  // The tracker reads the latest phase and clock through a ref, so it is created once.
  const ctx = useRef({ phase: ph?.state, dataVersion: f.snapshot?.version, offset: f.offset });
  ctx.current = {
    phase: ph?.state,
    dataVersion: f.snapshot?.version,
    offset: demoOffset ?? f.offset,
  };
  const tracker = useMemo(
    () =>
      injected ??
      createTracker({
        url: env.ingestUrl,
        now: () => Date.now() + ctx.current.offset,
        context: () => ({ phase: ctx.current.phase, dataVersion: ctx.current.dataVersion }),
      }),
    [injected],
  );
  useEffect(() => () => tracker.stop(), [tracker]);

  const ready = !!(st && f.snapshot);
  const viewed = useRef(false);
  useEffect(() => {
    if (ready && !viewed.current) {
      viewed.current = true;
      tracker.track('page_view');
    }
  }, [ready, tracker]);

  if (!st || !ph || !f.snapshot) {
    return (
      <div className="boot" role="status">
        {f.failures >= 2 ? (
          <>
            <h1>לא הצלחנו לטעון את האתר</h1>
            <p>בדקו את החיבור לאינטרנט. אנחנו ממשיכים לנסות ברקע.</p>
            <button className="btn btn-primary" onClick={() => feed.refresh()}>
              ניסיון חוזר
            </button>
          </>
        ) : (
          <p>טוען את יריד ההתמחויות…</p>
        )}
      </div>
    );
  }

  const stale = f.failures >= 3 || (f.lastOkAt > 0 && Date.now() - f.lastOkAt > STALE_AFTER_MS);
  const key = f.snapshot.version;

  return (
    <TrackerProvider tracker={tracker}>
      <div className="app">
        {env.demo && <DemoBar ph={ph} offset={demoOffset} onChange={setDemoOffset} />}
        <Site
          key={key}
          site={f.snapshot.site}
          st={st}
          ph={ph}
          units={f.snapshot.units}
          stale={stale}
        />
        <Toasts />
      </div>
    </TrackerProvider>
  );
}
